const CART_DRAWER_ADDED_NOTICE_TIMEOUT = 5000;
// How long the panel will wait on its images before opening regardless, and how many rows
// it waits for. Three covers what the drawer shows at a time; the rest load behind the
// scroll. The cap is generous next to the ~25ms a warm image takes, and short enough that
// a stalled one is not a drawer that fails to open.
const CART_DRAWER_MEDIA_WAIT = 500;
const CART_DRAWER_MEDIA_WAIT_ROWS = 3;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addedQuantity = 0;

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());

    // The overlay node is replaced on every re-render, so listen on the host
    // element rather than binding to the node itself.
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });

    this.bindOpenTriggers();
  }

  // Every cart change re-renders the panel body from Liquid, which replaces the
  // notice element with a hidden one. Repainting it from state on each change
  // keeps it up for its full five seconds even if the shopper edits a quantity
  // in the meantime.
  connectedCallback() {
    // `publish` runs subscribers unguarded, and product-form publishes before it
    // calls `renderContents` — a throw here would abort the add-to-cart itself.
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      try {
        this.renderAddedNotice();
      } catch (error) {
        console.error('[cart-drawer] added-notice repaint failed:', error);
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  // Anything that would otherwise navigate to the cart page — the header cart
  // icon, plus any element opting in with `data-cart-drawer-open` — opens the
  // drawer instead. Delegated from the document so triggers that get re-rendered
  // (the header cart bubble) keep working.
  bindOpenTriggers() {
    const headerCartIcon = document.getElementById('cart-icon-bubble');
    if (headerCartIcon) {
      headerCartIcon.setAttribute('role', 'button');
      headerCartIcon.setAttribute('aria-haspopup', 'dialog');
    }

    const openFromTrigger = (event) => {
      const trigger = event.target.closest('#cart-icon-bubble, [data-cart-drawer-open]');
      if (!trigger) return;
      // SparkLayer hides the drawer for B2B customers, who shop from their own
      // catalogue — let their clicks fall through to the cart page.
      if (getComputedStyle(this).display === 'none') return;
      event.preventDefault();
      this.open(trigger);
    };

    document.addEventListener('click', openFromTrigger);

    // Enter — including the numpad's — already reaches us as a click on a link
    // or a button. Space doesn't on a link, so it is handled here, and the
    // preventDefault also stops the page scrolling behind the drawer.
    document.addEventListener('keydown', (event) => {
      if (event.code !== 'Space') return;
      openFromTrigger(event);
    });
  }

  open(triggeredBy) {
    // Idempotent: a trigger can fire both a keydown and the browser's synthetic
    // click, and re-running this would stack `transitionend` listeners.
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // The transition doesn't reliably fire when the class lands in the same
    // frame as the visibility change, so defer it a tick.
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState) {
    const countBeforeAdd = this.getItemCount();

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHTML = parsedState.sections && parsedState.sections[section.id];
      if (!sectionElement || !sectionHTML) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
    });

    this.syncEmptyState();

    setTimeout(() => {
      this.setAddedNotice(Math.max(this.getItemCount() - countBeforeAdd, 0));
      this.waitForVisibleMedia().then(() => this.open());
    });
  }

  // An item's image is only requested once the new body is in the document, so opening in
  // the same tick slides an empty tile in front of the shopper and fills it in afterwards.
  // Nothing the page already downloaded can cover the gap: the drawer asks for its own
  // widths, so even a product whose card is on screen is a fresh request here.
  //
  // `decode()` rather than the load event — it resolves when the frame is ready to paint,
  // which is the thing being waited for. It is raced rather than awaited so a slow or
  // broken image can only ever cost CART_DRAWER_MEDIA_WAIT, never the drawer itself.
  waitForVisibleMedia() {
    const images = Array.from(this.querySelectorAll('.cart-drawer-item__image'))
      .slice(0, CART_DRAWER_MEDIA_WAIT_ROWS)
      .filter((image) => !image.complete);
    if (!images.length) return Promise.resolve();

    return Promise.race([
      Promise.all(images.map((image) => image.decode().catch(() => {}))),
      new Promise((resolve) => {
        setTimeout(resolve, CART_DRAWER_MEDIA_WAIT);
      }),
    ]);
  }

  // The class sits on this host, which none of the render paths replace, so it
  // has to be brought back in line with the body that was just written in. Left
  // stale it sends `open()` looking for the wrong focus container.
  syncEmptyState() {
    this.classList.toggle('is-empty', !this.querySelector('cart-drawer-items'));
  }

  // How many units the add actually put in the cart. It has to be a diff across
  // the re-render: `/cart/add.js` reports the line's *resulting* quantity, so
  // adding one more of something already in the cart would otherwise read as
  // however many are now on that line.
  getItemCount() {
    const header = this.querySelector('.drawer__header');
    return header ? Number(header.dataset.itemCount) || 0 : 0;
  }

  setAddedNotice(quantity) {
    this.addedQuantity = quantity;
    this.renderAddedNotice();

    clearTimeout(this.addedNoticeTimeout);
    if (!quantity) return;
    this.addedNoticeTimeout = setTimeout(() => {
      this.setAddedNotice(0);
    }, CART_DRAWER_ADDED_NOTICE_TIMEOUT);
  }

  // The drawer body is re-rendered from Liquid on every cart change, which
  // replaces the notice element — so the notice is painted from state here
  // rather than mutated in place.
  renderAddedNotice() {
    const notice = this.querySelector('#CartDrawer-AddedNotice');
    if (!notice) return;

    if (!this.addedQuantity) {
      notice.hidden = true;
      return;
    }

    const strings = (window.cartStrings && window.cartStrings.itemsAdded) || {};
    const template = this.addedQuantity === 1 ? strings.one : strings.other;
    notice.querySelector('.cart-drawer__added-notice-text').textContent = (template || '')
      .replace('[quantity]', this.addedQuantity);
    notice.hidden = false;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// These render the drawer from sections they requested, so reacting as well
// would cost a second fetch and visibly re-render the panel.
const CART_DRAWER_SELF_RENDERING_SOURCES = ['product-form', 'gift-auto-mark-gift'];

class CartDrawerItems extends CartItems {
  shouldIgnoreCartUpdate(event) {
    if (CART_DRAWER_SELF_RENDERING_SOURCES.includes(event.source)) return true;
    return super.shouldIgnoreCartUpdate(event);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // A cart change made outside the drawer (the cart page, the gift toggle, an
  // app) swaps the whole panel body: the empty state, the item list and the
  // footer are separate Liquid branches, so replacing them piecemeal can leave
  // the drawer showing items that are no longer in the cart.
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const freshDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const freshDrawer = freshDocument.querySelector('#CartDrawer');
        const liveDrawer = document.getElementById('CartDrawer');
        if (!freshDrawer || !liveDrawer) return;

        const drawer = liveDrawer.closest('cart-drawer');
        liveDrawer.innerHTML = freshDrawer.innerHTML;
        if (!drawer) return;

        drawer.syncEmptyState();
        drawer.renderAddedNotice();
      })
      .catch((error) => {
        window.Sentry && Sentry.captureException(error);
        console.error(error);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
